import { Controller } from "@hotwired/stimulus"

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

// The way out of a required question (DECISIONS D-7): an escape counts as an
// answer, so a worker who could not ask at 11pm can still save the visit.
//
// Kept behind a muted link so answering stays the obvious path. Choosing a
// reason mutes and disables the input it replaces — the escape IS the answer,
// so leaving a half-typed one posted alongside it would be a lie. Choosing the
// same reason again clears it and restores the input.
export default class extends Controller {
  static targets = ["fields", "reveal", "choices", "input", "chip"]

  declare readonly fieldsTarget: HTMLElement
  declare readonly revealTarget: HTMLElement
  declare readonly choicesTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly chipTargets: HTMLButtonElement[]

  // Options the server retired are disabled in the markup and must stay that
  // way — clearing an escape restores the question, not a withdrawn choice.
  private retired = new WeakSet<Field>()

  connect() {
    // visit-subject may have disabled the whole panel already (it marks what
    // it touched); only a field disabled on its own account is retired.
    this.fields().forEach((field) => { if (field.disabled && !field.dataset.subjectDisabled) this.retired.add(field) })
    // A 422 re-render arrives with the reason already chosen; it must not be
    // hidden behind the link the worker has already used.
    if (this.inputTarget.value) this.reveal()
    this.render()
  }

  reveal() {
    this.revealTarget.classList.add("hidden")
    this.choicesTarget.classList.remove("hidden")
  }

  choose(event: Event) {
    const chip = event.currentTarget as HTMLButtonElement

    this.inputTarget.value = this.inputTarget.value === chip.value ? "" : chip.value
    this.render()
  }

  private render() {
    const chosen = this.inputTarget.value

    this.chipTargets.forEach((chip) => {
      const active = chip.value === chosen
      chip.setAttribute("aria-pressed", String(active))
      chip.classList.toggle("border-primary", active)
      chip.classList.toggle("text-primary", active)
      chip.classList.toggle("text-slate-600", !active)
    })

    this.fieldsTarget.classList.toggle("opacity-40", Boolean(chosen))
    this.fields().forEach((field) => {
      field.disabled = this.retired.has(field) || Boolean(chosen)
    })
  }

  private fields(): Field[] {
    return Array.from(this.fieldsTarget.querySelectorAll<Field>("input, select, textarea"))
  }
}
